import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface VetStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
  detail: string;
}

interface HowWeVetEngineersProps {
  /** Controls the accent color theme */
  accentColor?: 'primary' | 'amber' | 'emerald' | 'rose' | 'yellow' | 'pink';
  /** Custom heading text */
  heading?: string;
  /** Industry-specific note shown in the footer badge */
  specialtyNote?: string;
  /** Pass rate — e.g. "12%" */
  passRate?: string;
  /** Optional: override individual steps */
  customSteps?: VetStep[];
}

const defaultSteps: VetStep[] = [
  {
    num: '01',
    icon: 'ri-file-list-3-line',
    title: 'Resume & Portfolio Deep Review',
    desc: 'We validate production experience, GitHub contributions, and shipped-product evidence before any engineer advances.',
    detail: '3+ years of real production code required',
  },
  {
    num: '02',
    icon: 'ri-video-chat-line',
    title: 'Live Technical Interview',
    desc: 'A 90-minute session covering system design, algorithmic thinking, and technology-specific depth with a senior staff engineer.',
    detail: 'Conducted by senior engineers, not recruiters',
  },
  {
    num: '03',
    icon: 'ri-code-s-slash-line',
    title: 'Real-World Code Challenge',
    desc: 'Candidates build a scoped, production-grade feature using your target stack — reviewed for architecture, code quality, and edge-case handling.',
    detail: 'Stack-matched to your project',
  },
  {
    num: '04',
    icon: 'ri-shield-check-line',
    title: 'Compliance & Domain Check',
    desc: 'We verify security awareness, regulatory knowledge (HIPAA, PCI-DSS, SOC 2), and relevant compliance certifications specific to your industry.',
    detail: 'Industry compliance verified',
  },
  {
    num: '05',
    icon: 'ri-user-voice-line',
    title: 'Communication & Culture Fit',
    desc: 'English fluency, remote discipline, async communication habits, and the ability to articulate technical decisions to non-technical stakeholders.',
    detail: 'English proficiency & async readiness',
  },
  {
    num: '06',
    icon: 'ri-medal-line',
    title: 'Reference & Background Check',
    desc: 'Past employer references, identity verification, and optional background screening — every engineer arrives with a verified track record.',
    detail: 'Full verification before any placement',
  },
];

const accentMap = {
  primary: {
    badge: 'bg-primary-50 border-primary-200',
    badgeText: 'text-primary-600',
    cardHover: 'hover:border-primary-200',
    iconBg: 'bg-primary-500',
    statText: 'text-primary-600',
    numText: 'text-primary-200',
    detailBg: 'bg-primary-50',
    detailText: 'text-primary-600',
  },
  amber: {
    badge: 'bg-amber-50 border-amber-200',
    badgeText: 'text-amber-600',
    cardHover: 'hover:border-amber-200',
    iconBg: 'bg-amber-500',
    statText: 'text-amber-600',
    numText: 'text-amber-200',
    detailBg: 'bg-amber-50',
    detailText: 'text-amber-600',
  },
  emerald: {
    badge: 'bg-emerald-50 border-emerald-200',
    badgeText: 'text-emerald-600',
    cardHover: 'hover:border-emerald-200',
    iconBg: 'bg-emerald-500',
    statText: 'text-emerald-600',
    numText: 'text-emerald-200',
    detailBg: 'bg-emerald-50',
    detailText: 'text-emerald-600',
  },
  rose: {
    badge: 'bg-rose-50 border-rose-200',
    badgeText: 'text-rose-600',
    cardHover: 'hover:border-rose-200',
    iconBg: 'bg-rose-500',
    statText: 'text-rose-600',
    numText: 'text-rose-200',
    detailBg: 'bg-rose-50',
    detailText: 'text-rose-600',
  },
  yellow: {
    badge: 'bg-yellow-50 border-yellow-200',
    badgeText: 'text-yellow-600',
    cardHover: 'hover:border-yellow-200',
    iconBg: 'bg-yellow-500',
    statText: 'text-yellow-600',
    numText: 'text-yellow-200',
    detailBg: 'bg-yellow-50',
    detailText: 'text-yellow-600',
  },
  pink: {
    badge: 'bg-pink-50 border-pink-200',
    badgeText: 'text-pink-600',
    cardHover: 'hover:border-pink-200',
    iconBg: 'bg-pink-500',
    statText: 'text-pink-600',
    numText: 'text-pink-200',
    detailBg: 'bg-pink-50',
    detailText: 'text-pink-600',
  },
};

export default function HowWeVetEngineers({
  accentColor = 'primary',
  heading = 'How We Vet Engineers',
  specialtyNote,
  passRate = '12%',
  customSteps,
}: HowWeVetEngineersProps) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const c = accentMap[accentColor];
  const steps = customSteps ?? defaultSteps;

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-gray-50/60 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-gray-50/60 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className={`inline-flex items-center space-x-2 px-4 py-2 ${c.badge} border rounded-full mb-4 md:mb-6 shadow-sm`}
          >
            <i className={`ri-shield-check-line ${c.badgeText} w-5 h-5 flex items-center justify-center`}></i>
            <span className="text-sm font-medium text-gray-700">Rigorous Screening Process</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            {heading}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Only <strong className={`font-bold ${c.statText}`}>{passRate}</strong> of applicants pass our full 6-stage screening. Every engineer reaches your shortlist already battle-tested, compliance-aware, and ready to ship.
          </motion.p>
        </motion.div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 25, scale: 0.97 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className={`group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 ${c.cardHover} hover:shadow-lg transition-all duration-300`}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className={`text-2xl font-black ${c.numText} leading-none mt-1`}>{step.num}</div>
                <div className={`w-12 h-12 flex items-center justify-center ${c.iconBg} rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${step.icon} text-xl text-white`}></i>
                </div>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{step.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">{step.desc}</p>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${c.detailBg} rounded-lg`}>
                <i className={`ri-check-line text-xs ${c.detailText}`}></i>
                <span className={`text-[10px] font-semibold ${c.detailText}`}>{step.detail}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, ...springTransition }}
          className="mt-10 md:mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-3xl mx-auto"
        >
          {[
            { label: 'Pass Rate', value: passRate, icon: 'ri-filter-line' },
            { label: 'Interview Duration', value: '90 min', icon: 'ri-time-line' },
            { label: 'Screening Stages', value: '6', icon: 'ri-list-check' },
            { label: 'Replacement Policy', value: '14-day', icon: 'ri-shield-check-line' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center text-center px-4 py-4 bg-gray-50 rounded-xl border border-gray-100"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-gray-100 mb-2">
                <i className={`${stat.icon} ${c.badgeText} text-sm`}></i>
              </div>
              <div className={`text-xl md:text-2xl font-bold ${c.statText}`}>{stat.value}</div>
              <div className="text-xs font-medium text-gray-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Optional specialty note */}
        {specialtyNote && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.7, ...springTransition }}
            className={`mt-6 md:mt-8 max-w-3xl mx-auto flex items-start gap-3 p-4 ${c.badge} border rounded-xl`}
          >
            <i className={`ri-information-line ${c.badgeText} text-lg flex-shrink-0 mt-0.5`}></i>
            <p className={`text-sm font-medium ${c.detailText} leading-relaxed`}>{specialtyNote}</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}